import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalSection,
  Paragraph,
  ModalFooter,
  Gutter,
  CondensedLarge,
  Flashy,
  Button,
  Form,
  ToastUtil,
  ApiError,
  NotFoundError,
  BadRequestError,
  Input,
  Grid,
  GridRow,
  GridColumn,
} from '@axiom/ui';
import type { MfaPhoneNumber, Candidate, User } from '@axiom/validation';
import { MfaVerificationSchema } from '@axiom/validation';

import { CandidateCommunicationAlertsApi } from '../../api/candidate-communication-alerts-api';
import { UsersApi } from '../../api/users-api';
import { CandidateApi } from '../../api/candidate-api';

export type PreferencesMfaPhoneVerifyModalProps = {
  name?: string;
  onClose: () => void;
  tempMfa: MfaPhoneNumber;
  userId: User['id'];
  candidateId: Candidate['id'];
};

export const PreferencesMfaPhoneVerifyModal = ({
  name = 'PREFERENCES_MFA_PHONE_VERIFY_MODAL',
  onClose,
  tempMfa,
  userId,
  candidateId,
}: PreferencesMfaPhoneVerifyModalProps) => {
  const resendTimeout = 600000;
  const fullPhoneNumber = `+${tempMfa.phoneNumberCountryCode} ${tempMfa.phoneNumber}`;

  const [isResendShown, setIsResendShown] = useState(false);
  const [timeoutCommand, setTimeoutCommand] = useState(Date.now());

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsResendShown(true);
    }, resendTimeout);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [timeoutCommand]);

  return (
    <Form
      name={`${name}_FORM`}
      schema={MfaVerificationSchema}
      initialValues={{
        mfaCode: undefined,
      }}
      onSubmit={async (formData, actions) => {
        try {
          await UsersApi.createMfaVerification(
            userId,
            MfaVerificationSchema.parse(formData)
          );
          await CandidateApi.refreshCandidateMfaPhoneNumber(candidateId);
          await CandidateCommunicationAlertsApi.refreshCommunicationAlerts(
            candidateId
          );
          ToastUtil.add({
            name: 'VERIFICATION_CODE_SUCCESS',
            type: 'info',
            children: (
              <CondensedLarge>
                You have successfully added a 2-factor authentication phone
                number
              </CondensedLarge>
            ),
          });

          onClose();
        } catch (error) {
          if (error instanceof ApiError) {
            if (error instanceof BadRequestError) {
              await actions.setFieldValue('mfaCode', null);
              actions.setFieldError(
                'mfaCode',
                'Invalid Code. Please request another one below.'
              );
            } else if (error instanceof NotFoundError) {
              await actions.setFieldValue('mfaCode', null);
              actions.setFieldError(
                'mfaCode',
                'Code Expired. Please request another one below.'
              );
            }
            setTimeoutCommand(Date.now());
            setIsResendShown(true);
          } else {
            throw error;
          }
        }
      }}
    >
      {({ fireSubmit, setValues }) => {
        const resendCode = async () => {
          await UsersApi.updateMfaPhoneNumber(userId, {
            phoneNumber: tempMfa.phoneNumber,
            phoneNumberCountryCode: tempMfa.phoneNumberCountryCode,
            phoneNumberCountry: tempMfa.phoneNumberCountry,
            registeredForMfa: null,
          });
          setIsResendShown(false);
          ToastUtil.add({
            name: 'VERIFICATION_CODE_RESEND',
            type: 'info',
            children: (
              <CondensedLarge>Code Sent to {fullPhoneNumber}</CondensedLarge>
            ),
          });
          setTimeoutCommand(Date.now());
          await setValues({ mfaCode: undefined });
        };

        return (
          <Modal name={name}>
            <ModalHeader
              name={`${name}_HEADER`}
              onClose={() => {
                onClose();
              }}
            >
              Add phone number
            </ModalHeader>
            <ModalSection>
              <Paragraph>
                We have sent a temporary code to {fullPhoneNumber}. Enter the
                code below to verify that it’s you. This code will expire after
                10 minutes.
              </Paragraph>
              <Gutter bottom="16px" />
              <Grid>
                <GridRow>
                  <GridColumn
                    mobileWidth={12}
                    tabletWidth={6}
                    largeScreenWidth={6}
                  >
                    <Input name="mfaCode" label="Enter the code" />
                    {isResendShown ? (
                      <Gutter top="16px">
                        <Flashy color="brandAxiomOrange">
                          <a
                            data-test="2FA_RESEND_CODE"
                            onClick={() => void resendCode()}
                          >
                            <CondensedLarge>Resend code</CondensedLarge>
                          </a>
                        </Flashy>
                      </Gutter>
                    ) : null}
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                name="CANCEL_BUTTON"
                variation="outline"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button name="SUBMIT_BUTTON" onClick={fireSubmit}>
                Verify
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
